import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue } from "./aiFieldProposalForm";

/**
 * GraphQL AI proposals store `fieldValue` as a string; the real entity/form field is typed (number, enum code, …).
 * BoundAi* uses `pendingProposedValue` for the highlight, `resolveAiProposalStatus` when the user edits,
 * and a `parseAi*` to coerce that string into the field's type. Accept All uses the same parsers via `aiFieldBinding`.
 */

/** Coerces GraphQL's string `fieldValue` into the entity field's type `T`. */
export type AiProposalParse<T> = (raw: string | null | undefined) => T | undefined;

/** If the proposal is pending, parse `fieldValue` into the entity field's type; otherwise undefined. */
export function pendingProposedValue<T>(
  aiProposal: ObjectState<AiFieldProposalFormValue> | undefined,
  parse: AiProposalParse<T>,
): T | undefined {
  if (aiProposal?.status.value !== AiFieldProposalStatus.Pending) return undefined;
  return parse(aiProposal.fieldValue.value);
}

/** Marks Accepted when `nextValue` matches the parsed proposal (entity field type); otherwise Rejected. */
export function resolveAiProposalStatus<T>(
  aiProposal: ObjectState<AiFieldProposalFormValue> | undefined,
  parse: AiProposalParse<T>,
  nextValue: T | undefined,
): void {
  if (!aiProposal) return;
  const proposed = parse(aiProposal.fieldValue.value);
  aiProposal.status.set(
    Object.is(proposed, nextValue) ? AiFieldProposalStatus.Accepted : AiFieldProposalStatus.Rejected,
  );
}

/** GraphQL `fieldValue` is always a string; number entity fields need a number. */
export function parseAiNumber(raw: string | null | undefined): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** Pass-through when the entity field is already a string (e.g. an enum `.code`). */
export function parseAiString(raw: string | null | undefined): string | undefined {
  if (raw == null || raw === "") return undefined;
  return raw;
}
